import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { consume, ContextType } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from './api.js';
import { linkStyles, pageStyles } from './components/cssstyles.js';
import { UserSelectedEvent } from './components/search-users.js';
import { contexts } from './contexts.js';
import { getInitials } from './utils.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

declare global {
  // This is embedded via esbuild define option.
  const COMMIT_SHA: string;
  const BUILD_TIMESTAMP: number;
  const PACKAGE_JSON_VERSION: string;
}

@customElement('page-administration-billing')
export class PageAdministrationBilling extends LitElement {
  static routes(): RouteConfig[] {
    return [{
      path: [
        '/administration/billing',
      ],
      render: () => html`<page-administration-billing></page-administration-billing>`,
    }];
  }

  static url(): string {
    return platformUrl('/administration/billing');
  }

  @consume({ context: contexts.serverInfo, subscribe: true }) @state() private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @state() private _trialResetUser?: WireTypes.User;
  @state() private _trialResetState?: { status: 'pending' } | { status: 'success', userName: string } | { status: 'error', message: string };

  private async _resetTrial() {
    const user = this._trialResetUser;
    if (!user)
      return;
    this._trialResetState = { status: 'pending' };
    try {
      await api.administration.resetUserTrial.POST({ userId: user.userId });
      this._trialResetState = { status: 'success', userName: user.userName };
      this._trialResetUser = undefined;
    } catch (e) {
      this._trialResetState = { status: 'error', message: (e as Error).message };
    }
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Product Plans · Dashboard' }),
  });

  override render() {
    return html`
      <user-header submenu=admin-billing></user-header>
      <app-body wide>
        <v-box>
          <div>
            Flakiness.io version <b><a-ext href=${`https://github.com/flakiness/flakiness/commits/${COMMIT_SHA}`}>v${PACKAGE_JSON_VERSION}</a-ext></b> built <b><sl-relative-time date=${new Date(BUILD_TIMESTAMP).toISOString()}></sl-relative-time></b>
          </div>
          <div>
            License valid until: <sl-format-date month="long" day="numeric" year="numeric" hour="numeric" minute="numeric" hour-format="12" date=${new Date(this._serverInfo?.deploymentLicense.validUntilMs!)}></sl-format-date>
          </div>
          <div>
            Billing is: <strong>${this._serverInfo?.enabledBilling ? 'enabled' : 'disabled'}</strong>
          </div>
          <div>
            Server hostname: <strong>${this._serverInfo?.hostname}</strong>
          </div>
        </v-box>
        ${this._serverInfo?.enabledBilling ? html`
          <product-plans show-plan-id></product-plans>
        ` : nothing}

        <sl-divider></sl-divider>
        <div class=section>
          <h2>Reset user trial</h2>
          <p>Clears the trial-used flag for the picked user so they can start a trial again.</p>
          ${this._trialResetUser ? html`
            <div class=picked-user>
              <sl-avatar
                image=${this._trialResetUser.avatarUrl ?? ''}
                initials=${getInitials(this._trialResetUser.userName)}
              ></sl-avatar>
              <span>${this._trialResetUser.userName}</span>
              <sl-button
                variant=danger
                size=small
                ?loading=${this._trialResetState?.status === 'pending'}
                @click=${() => this._resetTrial()}
              >Reset trial</sl-button>
              <sl-button
                size=small
                @click=${() => { this._trialResetUser = undefined; this._trialResetState = undefined; }}
              >Cancel</sl-button>
            </div>
          ` : html`
            <search-users
              placeholder="Pick a user to reset trial for"
              @user-selected=${(event: UserSelectedEvent) => {
                this._trialResetUser = event.user;
                this._trialResetState = undefined;
              }}
            ></search-users>
          `}
          ${this._trialResetState?.status === 'success' ? html`
            <sl-alert variant=success open closable @sl-after-hide=${() => this._trialResetState = undefined}>
              Trial reset for <strong>${this._trialResetState.userName}</strong>.
            </sl-alert>
          ` : nothing}
          ${this._trialResetState?.status === 'error' ? html`
            <sl-alert variant=danger open closable @sl-after-hide=${() => this._trialResetState = undefined}>
              ${this._trialResetState.message}
            </sl-alert>
          ` : nothing}
        </div>
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [pageStyles, linkStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }

    #plans {
      display: grid;
      grid-template-columns: auto auto auto auto auto;

      .header {
        border-bottom: 1px solid var(--fk-color-border);
        font-weight: var(--sl-font-weight-bold);
        padding: var(--sl-spacing-2x-small) 0;
      }

      .cell {
        padding: var(--sl-spacing-2x-small) 0;
      }
    }

    .section {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-small);
    }

    .picked-user {
      display: flex;
      align-items: center;
      gap: var(--sl-spacing-small);

      sl-avatar {
        --size: 1.5rem;
      }
    }
  `];
}
